import requireAuth from '@components/hoc/with-required-auth-hoc';
import useAnalysis, {
  AnalysisViewMode,
} from '@pages/app/analysis/analysis-overview-hook';
import ContentContainer from '@components/containers/content-container';
import { Typography } from '@mui/material';
import Row from '@components/layout-util-components/row';
import Spacer from '@components/layout-util-components/spacer';
import { LoadingIndicator } from '@components/loading-indicator';
import NoRcaInfo from '@pages/app/analysis/components/no-rca-info';
import StatsContainer from '@components/containers/stats-container';
import {
  DropDownFilter,
  SortByFilter,
  WCTTableFilters,
} from '@components/table';
import RadioFilter from '@components/table/filters/radio-filter';
import AnalysisTableView from '@pages/app/analysis/components/analysis-table-view';
import { ReactComponent as BarsIcon } from '@assets/svgs/bars.svg';
import { ReactComponent as Grid2Icon } from '@assets/svgs/grid-2.svg';
import { ReactComponent as LocationDotIcon } from '@assets/svgs/location-dot.svg';
import AnalysisGridView from '@pages/app/analysis/components/analysis-grid-view';
import { ButtonSkeleton } from '@components/skeletons';
import { useNavigate } from 'react-router-dom';
import AnalysisMapView from '@pages/app/analysis/components/analysis-map-view';
import useSearchParamsTyped from '@hooks/router-dom-helper-hooks';
import { numberFromString } from '@util/string-util';
import { Gap } from '@components/layout-util-components/gap';
import {
  faAward,
  faCrosshairs,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import PrimaryButton from '@components/buttons/primary-button';

interface Params {
  person?: string;
  group?: string;
  incidentLocation?: string;
}

function AnalysisOverviewPage() {
  const {
    person: personId,
    incidentLocation: incidentLocationId,
    group: groupId,
  } = useSearchParamsTyped<Params>();
  const navigate = useNavigate();

  const state = useAnalysis(
    numberFromString(personId),
    numberFromString(incidentLocationId),
    numberFromString(groupId)
  );

  const {
    isLoading,
    viewMode,
    search,
    status,
    person,
    group,
    sort,
    caseData,
    userOptions,
    statusOptions,
    groupOptions,
    sortByOptions,
  } = state;

  const onCreateRcaClick = () => {
    navigate('/rca/create');
  };

  return (
    <ContentContainer maxWidth={920}>
      <Row>
        <Typography fontSize={24} fontWeight={500} variant="h1">
          Analysis
        </Typography>
        <Spacer />
        {isLoading ? (
          <ButtonSkeleton noGrow />
        ) : caseData!.totalCount > 0 ? (
          <PrimaryButton buttonVariant="two" onClick={onCreateRcaClick}>
            <Typography
              fontSize={15}
              fontWeight={500}
              lineHeight="26px"
              letterSpacing="0.46px"
            >
              Create RCA
            </Typography>
          </PrimaryButton>
        ) : null}
      </Row>
      <Gap size={32} />
      {isLoading ? (
        <LoadingIndicator show />
      ) : caseData!.totalCount === 0 ? (
        <NoRcaInfo onCreateClicked={onCreateRcaClick} />
      ) : (
        <>
          <StatsContainer
            show
            stats={[
              {
                title: 'PROBLEMS',
                value: caseData!.counts!.problem,
                icon: faWarning,
                color: '#FF3D3D',
              },
              {
                title: 'RISKS',
                value: caseData!.counts!.risk,
                icon: faCrosshairs,
                color: '#FFB800',
              },
              {
                title: 'OPPURTUNITIES',
                value: caseData!.counts!.improvement,
                icon: faAward,
                color: '#46D4A2',
              },
            ]}
          />
          <Gap size={32} />
          <WCTTableFilters
            isLoading={isLoading}
            search={search.value}
            onSearch={search.set}
            rightOfSearchContent={
              <SortByFilter
                value={sort.value}
                onChange={sort.set}
                options={sortByOptions}
              />
            }
          >
            <DropDownFilter
              value={status.value}
              onChange={status.set}
              options={statusOptions ?? []}
              styledOptions={{
                marginLeft: 'auto',
                background: '#D8DBE3',
                border: 'none',
              }}
            >
              Status
            </DropDownFilter>
            <DropDownFilter
              value={person.value}
              onChange={person.set}
              options={userOptions ?? []}
              styledOptions={{
                background: '#D8DBE3',
                border: 'none',
              }}
            >
              Assigned
            </DropDownFilter>
            <DropDownFilter
              value={group.value}
              onChange={group.set}
              options={groupOptions ?? []}
              styledOptions={{
                background: '#D8DBE3',
                border: 'none',
              }}
            >
              Group
            </DropDownFilter>
            <RadioFilter
              options={[
                { id: AnalysisViewMode.grid, label: <Grid2Icon /> },
                { id: AnalysisViewMode.table, label: <BarsIcon /> },
                { id: AnalysisViewMode.map, label: <LocationDotIcon /> },
              ]}
              className="ml-auto"
              value={viewMode.value}
              onChange={viewMode.set}
            />
          </WCTTableFilters>
          <Gap size={24} />
          {viewMode.value === AnalysisViewMode.table ? (
            <AnalysisTableView {...state} />
          ) : viewMode.value === AnalysisViewMode.grid ? (
            <AnalysisGridView {...state} />
          ) : viewMode.value === AnalysisViewMode.map ? (
            <AnalysisMapView {...state} />
          ) : null}
        </>
      )}
    </ContentContainer>
  );
}

export default requireAuth(AnalysisOverviewPage);
